import { useState, useCallback } from 'react';
import { showToast } from '@repo/ui';
import { api } from '@xtnd/web/shared/api';

interface UsePreviewRecipientsReturn {
  refresh: () => Promise<void>;
  isRefreshing: boolean;
  toRecipients: string[];
  bccRecipients: string[];
  actualRecipients: string[];
  overrideActive: boolean;
  isLoading: boolean;
  /** Load initial recipient data (called when modal opens) */
  loadRecipients: (transactionId: string, matterId?: string) => Promise<void>;
  reset: () => void;
}

/**
 * Shared hook for loading and refreshing email recipients from Clio.
 * Used by both JuniperTransactionsPage and TrustRequestsTransactionsView
 * to avoid duplicating the override/refresh logic inline.
 */
export function usePreviewRecipients(): UsePreviewRecipientsReturn {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toRecipients, setToRecipients] = useState<string[]>([]);
  const [bccRecipients, setBccRecipients] = useState<string[]>([]);
  const [actualRecipients, setActualRecipients] = useState<string[]>([]);
  const [overrideActive, setOverrideActive] = useState(false);
  const [currentTransactionId, setCurrentTransactionId] = useState<string | null>(null);
  const [currentMatterId, setCurrentMatterId] = useState<string | undefined>(undefined);

  const applyOverrideLogic = useCallback(
    async (freshRecipients: string[]) => {
      const juniperConfig: any = await api.get('/juniper/config');
      const isOverride =
        !!juniperConfig.overrideClientEmails && !!juniperConfig.overrideEmailAddress;
      const bcc: string[] = Array.isArray(juniperConfig.emailBcc)
        ? juniperConfig.emailBcc.filter(Boolean)
        : [];

      setBccRecipients(bcc);
      setOverrideActive(isOverride);

      if (isOverride) {
        setToRecipients([juniperConfig.overrideEmailAddress]);
        setActualRecipients(freshRecipients);
      } else {
        setToRecipients(freshRecipients);
        setActualRecipients([]);
      }
    },
    []
  );

  /**
   * Load initial recipient data when the modal opens.
   * Extracts recipients from the transaction's local data and applies override logic.
   */
  const loadRecipients = useCallback(
    async (transactionId: string, matterId?: string) => {
      setCurrentTransactionId(transactionId);
      setCurrentMatterId(matterId);
      setIsLoading(true);
      try {
        const matterIdParam = matterId ? `?matterId=${matterId}` : '';
        const result: any = await api.get(
          `/juniper/transactions/${transactionId}/preview-recipients${matterIdParam}`
        );
        await applyOverrideLogic(result?.recipients || []);
      } catch {
        // Fallback: show empty state
        setToRecipients([]);
        setBccRecipients([]);
        setOverrideActive(false);
        setActualRecipients([]);
      } finally {
        setIsLoading(false);
      }
    },
    [applyOverrideLogic]
  );

  /**
   * Refresh recipients from Clio (on-demand button click).
   * Re-fetches fresh data via the preview-recipients endpoint.
   */
  const refresh = useCallback(async () => {
    if (!currentTransactionId) return;
    setIsRefreshing(true);
    try {
      const matterIdParam = currentMatterId ? `?matterId=${currentMatterId}` : '';
      const result: any = await api.get(
        `/juniper/transactions/${currentTransactionId}/preview-recipients${matterIdParam}`
      );
      if (result?.recipients) {
        await applyOverrideLogic(result.recipients);
        showToast.success(
          `Refreshed: ${result.recipients.length} recipient${result.recipients.length === 1 ? '' : 's'} found`
        );
      }
    } catch {
      showToast.error('Failed to refresh contacts from Clio');
    } finally {
      setIsRefreshing(false);
    }
  }, [currentTransactionId, currentMatterId, applyOverrideLogic]);

  const reset = useCallback(() => {
    setToRecipients([]);
    setBccRecipients([]);
    setActualRecipients([]);
    setOverrideActive(false);
    setCurrentTransactionId(null);
    setCurrentMatterId(undefined);
    setIsLoading(false);
    setIsRefreshing(false);
  }, []);

  return {
    refresh,
    isRefreshing,
    toRecipients,
    bccRecipients,
    actualRecipients,
    overrideActive,
    isLoading,
    loadRecipients,
    reset,
  };
}
